import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { buildPedalPricingKey, evaluateMarketTier } from '../utils/reverbPricing';
import { getDevPricingOverrides, resolveDevOverride } from '../utils/devPricingOverrides';
import { verifiedFreshDoc, heldDoc, subscribeReturnToView, storedDocMatchesIdentity } from '../utils/pricingDocCache';

/**
 * Batch market-value lookup for a set of pedals, for screens that need pricing
 * on many pedals at once (offer composition grids).
 *
 * Reads pedalPricing/{key} straight from Firestore rather than going through
 * fetchReverbPricingOnDemand. The callable sits in the heavy functions codebase
 * with a ~6-7s cold start (see useReverbPricing), which is tolerable for the one
 * lookup on a listing page but not for a whole pedalboard. Consequence: pedals
 * with no cached doc yet simply have no market value here. The weekly refresh
 * and listing-page views populate the collection over time.
 */

// Shared across mounts, TTL-BOUNDED (pricingDocCache.js): asking prices
// change at most weekly, but transaction evidence changes docs at event
// time — a reversal must not survive the whole session in this cache.
const docCache = new Map(); // pricingKey -> { data: doc | null, at }

// Expiry re-checks are chunked rather than one long setTimeout, for the same
// reasons as PriceIndicator: browser timers cap at ~24.8 days and background
// tabs throttle, so each chunk re-measures what is left.
const EXPIRY_RECHECK_CHUNK_MS = 6 * 60 * 60 * 1000;

export function usePedalMarketValues(pedals) {
  const signature = (pedals || [])
    .map((p) => `${p?.id}:${buildPedalPricingKey(p?.make, p?.model, p?.decade)}:${p?.condition}`)
    .join('|');

  const entries = useMemo(
    () =>
      (pedals || [])
        .filter((p) => p && p.id)
        .map((p) => ({
          id: p.id,
          key: buildPedalPricingKey(p.make, p.model, p.decade),
          // Raw identity is carried so a fetched document can be VERIFIED
          // against the product actually being valued — see the fetch below.
          make: p.make,
          model: p.model,
          condition: p.condition
        }))
        .filter((e) => e.key),
    // Rebuild only when the pedals actually change, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  const [marketTiers, setMarketTiers] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  // The earliest expiry stamp among the tiers currently displayed, or null
  // when none of them is transaction-informed. Drives the re-evaluation
  // timer below; see buildTiers for why it is tracked at all.
  const [expiryStamp, setExpiryStamp] = useState(null);

  // Evaluate the documents this hook is HOLDING, with no network access of
  // any kind. Used both after a lookup and for expiry re-evaluation, so the
  // two cannot drift apart.
  //
  // heldDoc (not freshCachedDoc) is deliberate on the re-evaluation path: a
  // re-evaluation has no fetch behind it, so treating a doc as absent
  // because the 10-minute lookup TTL lapsed would BLANK a displayed value
  // and put nothing in its place. The expiry that genuinely must withdraw
  // numbers is the server's staleAfterMs stamp, which evaluateMarketTier
  // enforces itself. On the post-lookup path the two are equivalent: every
  // stale key was just refetched (see missingKeys).
  const buildTiers = useCallback(() => {
    const next = new Map();
    let earliest = Infinity;
    for (const entry of entries) {
      // v2 evaluator: a pedal's own live listing is excluded from its own
      // benchmark (excludeListingId), and only display-gated data yields a
      // tier. Consumers here use tiers for VALUE math/ranges, never for
      // good/high verdicts, so every non-'none' kind maps to its range.
      // IDENTITY VERIFICATION AT READ TIME (review point 2). Verifying only
      // freshly FETCHED documents was insufficient: a document already
      // sitting in this hook's cache — put there by an earlier render, an
      // earlier pedal set, or a build that predates the verification — would
      // be evaluated unchecked. Both caches are read through here, so this
      // is the one place that covers every held document regardless of how
      // it arrived. A document that cannot prove it belongs to this pedal
      // yields no tier, exactly as an absent one does.
      const held = heldDoc(docCache.get(entry.key));
      const verified = held && storedDocMatchesIdentity(held, entry.make, entry.model)
        ? held : null;
      const evaluation = evaluateMarketTier(
        verified,
        entry.condition,
        { excludeListingId: entry.id }
      );
      next.set(entry.id, evaluation.kind === 'none' ? null : evaluation.tier);
      if (evaluation.transactionInformed && Number.isFinite(evaluation.staleAfterMs)) {
        earliest = Math.min(earliest, evaluation.staleAfterMs);
      }
    }
    setMarketTiers(next);
    setExpiryStamp(Number.isFinite(earliest) ? earliest : null);
  }, [entries]);

  useEffect(() => {
    let cancelled = false;

    const applyTiers = () => {
      if (cancelled) return;
      buildTiers();
      setLoading(false);
    };

    // One raw identity per pricing key, used as the FETCH REPRESENTATIVE —
    // whose make/model the fetched document is checked against.
    //
    // It is NOT a claim that every entry under a key has the same identity.
    // The key is lossy: non-ASCII letters still fold to a separator, so two
    // different names can in principle still share one key (measured scope
    // today: 2 affected catalog entries, 0 collisions — see
    // functions/test/pricingKeyParity.test.js). READ-TIME verification in
    // buildTiers is what protects against a remaining collision, because it
    // checks each entry against ITS OWN make/model rather than against the
    // representative.
    const identityByKey = new Map();
    for (const e of entries) {
      if (!identityByKey.has(e.key)) identityByKey.set(e.key, { make: e.make, model: e.model });
    }

    // An entry that cannot be VERIFIED counts as missing, so it is refetched
    // and overwritten rather than sitting in the cache unusable (review
    // point 2: documents already in the cache must be verified too).
    const missingKeys = [...new Set(entries.map((e) => e.key))]
      .filter((k) => {
        const ident = identityByKey.get(k) || {};
        return verifiedFreshDoc(docCache.get(k), ident.make, ident.model) === undefined;
      });
    if (missingKeys.length === 0) {
      applyTiers();
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    // Dev-only v2 preview overlay (no-op in production builds).
    getDevPricingOverrides().then((overrides) => Promise.all(
      missingKeys.map(async (key) => {
        const resolved = resolveDevOverride(overrides, key);
        if (resolved !== undefined) {
          // Verified like any other document. This is the ONE path that puts
          // an entry into the cache without going through the fetch check
          // above, so leaving it unverified would be the gap that makes the
          // read-time check in buildTiers the only line of defence. The
          // generated fixtures all carry make/model, so a correct override
          // passes; a mis-keyed one is refused rather than priced.
          const ident = identityByKey.get(key) || {};
          const ok = resolved === null ||
            storedDocMatchesIdentity(resolved, ident.make, ident.model);
          docCache.set(key, { data: ok ? resolved : null, at: Date.now() });
          return;
        }
        try {
          const snap = await getDoc(doc(db, 'pedalPricing', key));
          const raw = snap.exists() ? snap.data() : null;
          // IDENTITY VERIFICATION — FAIL CLOSED, the same rule the logged-out
          // useReverbPricing read applies.
          //
          // This path was missed in the first pass: an ambiguous legacy
          // document could be REFUSED on a listing page and still used in
          // offer-grid trade-balance maths, because this hook reads
          // pedalPricing directly and cached whatever came back. Before '+'
          // was preserved in the key, DigiTech Trio and Trio+ both resolved
          // to digitech_trio_modern, so a document found under a legacy key
          // may belong to the other product. A document that cannot prove it
          // is this product is cached as a MISS, so the grid values that
          // pedal from nothing rather than from another pedal's prices.
          const ident = identityByKey.get(key) || {};
          const data = raw && storedDocMatchesIdentity(raw, ident.make, ident.model)
            ? raw : null;
          docCache.set(key, { data, at: Date.now() });
        } catch {
          // Cache the miss so a broken lookup doesn't retry on every render
          // (the TTL retries it eventually).
          docCache.set(key, { data: null, at: Date.now() });
        }
      })
    )).then(applyTiers);

    return () => {
      cancelled = true;
    };
  }, [entries, buildTiers]);

  // EXPIRY RE-EVALUATION FOR A MOUNTED GRID (owner instruction, 2026-10-12:
  // "expiry re-evaluation for mounted offer grids").
  //
  // Before this, these grids had no expiry mechanism at all: evaluateMarketTier
  // drops an expired transaction-informed entry at the moment it runs, but a
  // grid only ran it on mount or when the pedal set changed. A trade
  // composition screen left open could therefore keep doing balance math on a
  // band whose evidence window had already closed — the one thing the
  // staleAfterMs stamp exists to prevent. PriceIndicator had the timer; these
  // grids did not.
  //
  // This is RE-EVALUATION, NOT A REFETCH, and that distinction is the point:
  // it re-runs the evaluator over the documents already held, so an expired
  // band disappears with ZERO network calls. It never adds a request, and
  // these grids do NOT take part in the bounded return-to-view refresh —
  // their lookups still happen on mount and on pedal-set change, as before.
  //
  // Scheduling mirrors PriceIndicator exactly, including the boundary
  // contract: an entry is valid THROUGH staleAfterMs inclusive and expired
  // strictly after it, so a callback landing exactly AT the stamp re-arms a
  // 1ms recheck instead of leaving a still-valid band with no pending timer.
  // The shared return-to-view signal covers throttled/suspended timers.
  useEffect(() => {
    if (!Number.isFinite(expiryStamp)) return undefined;
    const stamp = expiryStamp;
    let timerId = null;
    let disposed = false;
    const arm = () => {
      if (disposed) return;
      const remaining = stamp - Date.now();
      if (remaining < 0) {
        buildTiers(); // strictly past the stamp: re-evaluate what we hold
        return;
      }
      timerId = setTimeout(arm, Math.min(remaining + 1, EXPIRY_RECHECK_CHUNK_MS));
    };
    const onReturnToView = () => {
      if (Date.now() > stamp) buildTiers();
    };
    arm();
    const unsubscribe = subscribeReturnToView(onReturnToView);
    return () => {
      disposed = true;
      if (timerId) clearTimeout(timerId);
      unsubscribe();
    };
  }, [expiryStamp, buildTiers]);

  return { marketTiers, loading };
}
